import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from '../../components/organisms';
import { Button } from '../../components/atoms';
import { useSeries, useSeriesActions } from '../../hooks/useSeries';
import { resolveAssetUrl } from '../../utils/url';
import type { Column, TableAction } from '../../types/table';
import type { Drama } from '../../types/api/drama';
import { formatDateForDisplay } from '../../types/api/drama';
import toast from 'react-hot-toast';
import { DeleteConfirmationModal } from '../../components/organisms/DeleteConfirmationModal';

/**
 * SeriesManagementPage Component
 *
 * Admin page for managing series content backed by the shared Drama aggregate.
 * Features:
 * - Server-side pagination
 * - Keyword search
 * - Status & access badges
 * - Seasons/episodes overview
 * - CRUD entry points (create, edit, view, delete)
 */
export const SeriesManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { fetchSeries } = useSeries();
  const { softDeleteSeries } = useSeriesActions();
  const [refreshKey, setRefreshKey] = useState(0);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedSeries, setSelectedSeries] = useState<Drama | null>(null);

  const handleRefresh = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  const handleCreate = useCallback(() => {
    navigate('/pages/series/create');
  }, [navigate]);

  const handleEdit = useCallback(
    (series: Drama) => {
      navigate(`/pages/series/${series.dramaId}/edit`);
    },
    [navigate]
  );

  const handleView = useCallback(
    (series: Drama) => {
      navigate(`/pages/series/${series.dramaId}`);
    },
    [navigate]
  );

  const handleDelete = useCallback((series: Drama) => {
    setSelectedSeries(series);
    setIsDeleteModalOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!selectedSeries) return;

    try {
      await softDeleteSeries(selectedSeries.contentId);
      toast.success('Series deleted successfully');
      setIsDeleteModalOpen(false);
      setSelectedSeries(null);
      handleRefresh();
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete series');
    }
  }, [selectedSeries, softDeleteSeries, handleRefresh]);

  const columns = useMemo<Column<Drama>[]>(
    () => [
      {
        id: 'title',
        header: 'Series',
        accessor: 'title',
        cell: (row) => (
          <div className="flex items-center gap-3">
            {/* Thumbnail */}
            {row.thumbnailUrl ? (
              <img
                src={resolveAssetUrl(row.thumbnailUrl)}
                alt={row.title}
                className="w-16 h-24 object-cover rounded-lg flex-shrink-0"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const fallback = target.nextElementSibling as HTMLElement;
                  if (fallback) fallback.style.display = 'flex';
                }}
              />
            ) : null}
            <div
              className="w-16 h-24 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ display: row.thumbnailUrl ? 'none' : 'flex' }}
            >
              <svg
                className="w-8 h-8 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
            {/* Series Info */}
            <div className="min-w-0 flex-1 text-left">
              <p className="text-sm font-semibold text-gray-900 truncate">{row.title}</p>
              <p className="text-xs text-gray-500 truncate">{row.languageName}</p>
              <p className="text-xs text-gray-400 mt-1">
                S{row.totalSeasons} • {row.totalEpisodes} episodes • {row.episodeDuration} min
              </p>
            </div>
          </div>
        ),
        minWidth: '260px',
      },
      {
        id: 'description',
        header: 'Description',
        accessor: 'description',
        cell: (row) => (
          <p className="text-sm text-gray-700 line-clamp-2 max-w-xs text-left">
            {row.description}
          </p>
        ),
        hideOnMobile: true,
      },
      {
        id: 'status',
        header: 'Status',
        accessor: 'status',
        cell: (row) => {
          const statusColors: Record<string, string> = {
            ACTIVE: 'bg-green-100 text-green-800',
            INACTIVE: 'bg-gray-100 text-gray-800',
            PENDING: 'bg-yellow-100 text-yellow-800',
          };

          return (
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                statusColors[row.status] || 'bg-gray-100 text-gray-800'
              }`}
            >
              {row.status}
            </span>
          );
        },
      },
      {
        id: 'accessLevel',
        header: 'Access',
        accessor: 'accessLevel',
        cell: (row) => {
          const accessColors: Record<string, string> = {
            FREE: 'bg-blue-100 text-blue-800',
            PREMIUM: 'bg-purple-100 text-purple-800',
          };

          return (
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                accessColors[row.accessLevel] || 'bg-gray-100 text-gray-800'
              }`}
            >
              {row.accessLevel}
            </span>
          );
        },
        hideOnMobile: true,
      },
      {
        id: 'rating',
        header: 'Rating',
        accessor: 'averageRating',
        cell: (row) => (
          <span className="text-sm text-gray-700 text-left">
            {row.ratingCount > 0 ? `${row.averageRating.toFixed(1)} ★ (${row.ratingCount})` : '—'}
          </span>
        ),
        hideOnMobile: true,
      },
      {
        id: 'uploadDate',
        header: 'Uploaded',
        accessor: 'uploadDate',
        cell: (row) => (
          <span className="text-sm text-gray-600 text-left">
            {formatDateForDisplay(row.uploadDate)}
          </span>
        ),
        hideOnMobile: true,
      },
    ],
    []
  );

  const actions = useMemo<TableAction<Drama>[]>(
    () => [
      {
        id: 'view',
        label: 'View',
        variant: 'ghost',
        icon: (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
            <path
              fillRule="evenodd"
              d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
              clipRule="evenodd"
            />
          </svg>
        ),
        onClick: handleView,
      },
      {
        id: 'edit',
        label: 'Edit',
        variant: 'ghost',
        icon: (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
          </svg>
        ),
        onClick: handleEdit,
      },
      {
        id: 'disable',
        label: 'Disable',
        variant: 'danger',
        icon: (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        ),
        onClick: handleDelete,
        show: (row) => row.status !== 'INACTIVE',
      },
    ],
    [handleView, handleEdit, handleDelete]
  );

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="text-left">
          <h1 className="text-lg font-bold text-gray-900">Series Management</h1>
          <p className="text-gray-600 mt-1 pl-1">
            Manage series content backed by the Drama aggregate
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3">
          <Button
            onClick={handleCreate}
            leftIcon={
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                  clipRule="evenodd"
                />
              </svg>
            }
          >
            Add Series
          </Button>
        </div>
      </div>

      {/* DataTable */}
      <DataTable<Drama>
        columns={columns}
        fetchData={fetchSeries}
        searchable
        searchPlaceholder="Search series by title or keyword..."
        initialPageSize={10}
        pageSizeOptions={[10, 20, 50, 100]}
        actions={actions}
        hoverable
        showRefreshButton
        onRefresh={handleRefresh}
        caption="Series management table"
        key={refreshKey}
        renderHeaderActions={({ setFilter, removeFilter, filters }) => {
          const accessLevelFilter = filters.find(f => f.columnId === 'accessLevel');
          const currentValue = accessLevelFilter?.value || '';
          const contentRegionFilter = filters.find(f => f.columnId === 'contentRegion');
          const contentRegionValue = contentRegionFilter?.value || '';

          return (
            <div className="flex items-center gap-2 flex-wrap">
              <label htmlFor="content-region-filter" className="text-sm font-medium text-gray-700 whitespace-nowrap">
                Content Region:
              </label>
              <select
                id="content-region-filter"
                value={contentRegionValue}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '') {
                    removeFilter('contentRegion');
                  } else {
                    setFilter({
                      columnId: 'contentRegion',
                      operator: 'equals',
                      value: value,
                    });
                  }
                }}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30 focus:border-[#EAB308]"
              >
                <option value="">All</option>
                <option value="myanmar">Myanmar</option>
                <option value="international">International</option>
              </select>
              <label htmlFor="access-level-filter" className="text-sm font-medium text-gray-700 whitespace-nowrap">
                Access Level:
              </label>
              <select
                id="access-level-filter"
                value={currentValue}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '') {
                    removeFilter('accessLevel');
                  } else {
                    setFilter({
                      columnId: 'accessLevel',
                      operator: 'equals',
                      value: value,
                    });
                  }
                }}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30 focus:border-[#EAB308]"
              >
                <option value="">All</option>
                <option value="FREE">FREE</option>
                <option value="PREMIUM">PREMIUM</option>
              </select>
            </div>
          );
        }}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedSeries(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Disable Series"
        message={
          selectedSeries
            ? `Are you sure you want to disable "${selectedSeries.title}"? This will soft delete the series (mark it as inactive). You can re-enable it from the series detail page.`
            : 'Are you sure you want to disable this series? This will mark it as inactive.'
        }
      />
    </div>
  );
};

export default SeriesManagementPage;


